<template>
  <v-card class="pa-3 mx-auto media-list-content" min-width="100" :max-width="MAX_CONTENT_WIDTH">
    <v-text-field
      v-model="editedFields.title"
      class="title-input mb-2"
      outlined
      color="secondary"
      dense
      no-resize
      height="34"
      hide-details
    ></v-text-field>
    <v-textarea
      v-model="editedFields.description"
      placeholder="description"
      rows="1"
      auto-grow
      class="description-input"
      outlined
      color="secondary"
      dense
      hide-details
      row-height="20"
    ></v-textarea>

    <div class="d-flex my-2">
      <v-checkbox
        v-model="editedFields.isPrivate"
        label="private"
        color="primaryText"
        hide-details
        class="mt-0 pt-0"
      ></v-checkbox>
      <v-checkbox
        v-model="editedFields.isRanked"
        label="ranked"
        color="primaryText"
        hide-details
        class="mt-0 pt-0 ml-4"
      ></v-checkbox>
    </div>

    <v-divider></v-divider>

    <div class="interactions">
      <v-btn @click="onSave" elevation="0" color="primary" small>save</v-btn>
      <v-spacer></v-spacer>
      <select-dense placeholder="reorder" :items="['foo', 'bar']"></select-dense>
    </div>

    <v-divider></v-divider>

    <div class="d-flex justify-center my-2">
      <v-btn large outlined @click="mediaSearchEnabled = true" color="text lighten-1"><v-icon>mdi-plus</v-icon></v-btn>
    </div>

    <draggable
      v-model="editableListEntries"
      @start="dragging = true"
      @end="onDragEnd"
      handle=".handle"
      :animation="200"
      :scroll-sensitivity="200"
      :force-fallback="true"
      :scroll-speed="15"
    >
      <div v-for="(entry, idx) in editableListEntries" :key="entry.work.id + entry.work.medium">
        <media-list-entry
          :workId="entry.work.id"
          :medium="entry.work.medium"
          :title="entry.work.title"
          :posterSource="entry.work.posterSource"
          :releaseDate="entry.work.releaseDate"
          :position="isRanked ? entry.position + 1 : undefined"
          display-medium
          :edit-mode="true"
          :index="idx"
          @delete-entry="onDeleteEntry(entry)"
        ></media-list-entry>
        <v-divider></v-divider>
      </div>
    </draggable>

    <media-search-popup
      :visible.sync="mediaSearchEnabled"
      @workSelected="onMediaSearchWorkSelected"
    ></media-search-popup>
  </v-card>
</template>

<script>
import { mapMutations } from "vuex";

import { MAX_CONTENT_WIDTH } from "../util/constants";
import MediaListEntry from "./media-list-entry.vue";
import draggable from "vuedraggable";
import SelectDense from "./select-dense.vue";
import MediaSearchPopup from "./media-search-popup.vue";
// eslint-disable-next-line no-unused-vars
import * as MediaSearchTypes from "./media-search.vue";
// eslint-disable-next-line no-unused-vars
import * as Types from "../types/typedefs";

/**
 * @typedef {Object} DragEndEvent
 * @property {number} oldIndex
 * @property {number} newIndex
 */

/**
 * @typedef {Object} EditableListEntry
 * @property {Number} position
 * @property {Object} work
 * @property {String} work.id
 * @property {Types.Medium} work.medium
 * @property {String} work.title
 * @property {String} work.posterSource
 * @property {String} work.releaseDate
 */

/**
 * @typedef {Object} ListEditAction
 * @property {('DELETE'|'MOVE'|'ADD')} action
 * @property {String} workId
 * @property {Types.Medium} medium
 * @property {Number} [position]
 */

export const LEAVE_CONFIRMATION_MESSAGE = "Are you sure you want to leave? You still have unsaved changes";

function beforeWindowUnload(event) {
  event.preventDefault();
  return LEAVE_CONFIRMATION_MESSAGE;
}

export default {
  name: "media-list-edit-content",

  components: {
    MediaListEntry,
    draggable,
    SelectDense,
    MediaSearchPopup,
  },

  props: {
    listId: String,
    title: String,
    description: String,
    owner: Object,
    isRanked: Boolean,
    isPrivate: Boolean,
    listEntries: Array,
  },

  data: () => ({
    MAX_CONTENT_WIDTH,

    dragging: false,

    editedFields: {
      title: "",
      description: undefined,
      isPrivate: null,
      isRanked: null,
    },

    /**
     * A shallow copy of the passed `listEntries` prop that can be modified without changing the original
     * @type {Array<EditableListEntry>}
     */
    editableListEntries: [],

    /**
     * Records list updates, including new entries, re-ordered entries, and removed entries.
     * @type ListEditAction
     */
    listEntryEdits: [],

    mediaSearchEnabled: false,
  }),

  watch: {
    title(newValue) {
      this.editedFields.title = newValue;
    },
    description(newValue) {
      this.editedFields.description = newValue;
    },
    isRanked(newValue) {
      this.editedFields.isRanked = newValue;
    },
    isPrivate(newValue) {
      this.editedFields.isPrivate = newValue;
    },
  },

  mounted() {
    this.editedFields.title = this.title;
    this.editedFields.description = this.description;
    this.editedFields.isRanked = this.isRanked;
    this.editedFields.isPrivate = this.isPrivate;
    this.editableListEntries = [...this.listEntries];

    window.addEventListener("beforeunload", beforeWindowUnload);
  },

  beforeDestroy() {
    window.removeEventListener("beforeunload", beforeWindowUnload);
  },

  methods: {
    ...mapMutations(["showSnackbar"]),

    /**
     * @param {DragEndEvent} event
     */
    onDragEnd(event) {
      this.dragging = false;

      const movedItem = this.editableListEntries[event.newIndex];
      this.listEntryEdits.push({
        action: "MOVE",
        workId: movedItem.work.id,
        medium: movedItem.work.medium,
        position: event.newIndex,
      });
    },

    async onSave() {
      // TODO: Perform validation

      try {
        if (this.editedFieldsUpdated()) {
          await this.$axios.patch(`/v1/lists/${this.listId}`, {
            title: this.editedFields.title ? this.editedFields.title : undefined,
            description: this.editedFields.description,
            isRanked: this.editedFields.isRanked,
            isPrivate: this.editedFields.isPrivate,
          });
        }

        if (this.listEntryEdits.length > 0) {
          await this.$axios.post(`/v1/lists/${this.listId}/bulk-edits`, {
            actions: this.listEntryEdits,
          });
        }

        this.$emit("edits-saved", this.editedFields);

        const query = Object.assign({}, this.$route.query);
        delete query.edit;
        this.$router.replace({
          name: this.$route.name,
          query,
        });
      } catch (err) {
        if (err.response && err.response.status === 400) {
          this.showSnackbar({
            text: "Your list failed to update due to a validation error",
            error: true,
          });
        } else {
          this.showSnackbar({
            text: "An unexpected error occurred",
            error: true,
          });
        }
      }
    },

    /**
     * @param {EditableListEntry} entry
     */
    onDeleteEntry(entry) {
      console.debug(`Handling delete for entry [${entry}]`);
      this.listEntryEdits.push({
        action: "DELETE",
        medium: entry.work.medium,
        workId: entry.work.id,
      });

      this.editableListEntries = this.editableListEntries.filter(
        (item) => item.work.id !== entry.work.id || item.work.medium != entry.work.medium,
      );
    },

    /**
     * @returns {Boolean}
     */
    editedFieldsUpdated() {
      return (
        this.editedFields.title != this.title ||
        this.editedFields.description != this.description ||
        this.editedFields.isPrivate != this.isPrivate ||
        this.editedFields.isRanked != this.isRanked
      );
    },

    hasChanges() {
      return this.editedFieldsUpdated || this.listEntryEdits.length > 0;
    },

    /**
     * @param {MediaSearchTypes.WorkSelectedEvent} event
     */
    onMediaSearchWorkSelected(event) {
      console.debug(`Handling workSelected event [${event}]`);
      const position = 0;
      this.listEntryEdits.push({
        action: "ADD",
        workId: event.id,
        medium: event.medium,
        position,
      });

      this.editableListEntries.unshift({
        position,
        work: {
          id: event.id,
          medium: event.medium,
          title: event.title,
          posterSource: event.poster_source,
        },
      });
    },
  },
};
</script>

<style scoped>
.title-input {
  font-size: 24px;
  font-weight: bold;
}

.description-input {
  font-size: 14px;
}
.description-input >>> .v-input__slot {
  min-height: 0px !important;
}
.description-input >>> textarea {
  line-height: 20px;
  margin: 8px 0;
  min-height: 0px;
}
.v-input.description-input >>> input {
  color: var(--v-text-lighten1);
}
</style>
